/**
 * Plan anlegen (docs/planverortung-vertrag.md, P2): Datei waehlen →
 * zuschneiden und benennen → hochladen → `onAngelegt`.
 *
 * Baustein fuer die Plan-Verwaltung (SafetyPointsView), den
 * Verortungsdialog und den Handy-Assistenten. Ohne `children` erscheint der
 * Standardknopf „Plan hochladen“; mit `children` baut der Aufrufer seinen
 * eigenen Ausloeser: `children(oeffnen, laedt)`.
 *
 * Die Zuschneider liegen per Portal an `document.body` (z-[140]) — ueber
 * einem Vollbild-Dialog mit z-[120], auch wenn der Ausloeser in einem
 * transformierten Container steckt.
 */
import React, { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { createPortal } from 'react-dom';
import { FileUp, Loader2, X } from 'lucide-react';
import type { ProjectPlan } from '../../types';
import { Button } from '../Button';
import { PdfCropper } from '../PdfCropper';
import { uploadAsset } from '../../services/cloudService';
import { PlanBildZuschnitt } from './PlanBildZuschnitt';
import { planNameAusDatei, planRueckfallName } from './planFormat';

export interface PlanAnlegenProps {
    projectId: string;
    sectionId?: string;
    /** Rueckfallname „Plan n+1“. */
    anzahlVorhanden: number;
    /** Nach erfolgreichem Upload. */
    onAngelegt: (plan: ProjectPlan) => void;
    /** Eigener Ausloeser statt des Standardknopfs. */
    children?: (oeffnen: () => void, laedt: boolean) => React.ReactNode;
    /** Klassen fuer den Standardknopf „Plan hochladen“. */
    className?: string;
}

type Zuschnitt =
    | { art: 'pdf'; datei: File; startName: string }
    | { art: 'bild'; daten: string; startName: string };

const LESEFEHLER = 'Die Datei konnte nicht gelesen werden.';

const istPdf = (datei: File): boolean => datei.type === 'application/pdf' || /\.pdf$/i.test(datei.name);

export function PlanAnlegen({
    projectId,
    sectionId,
    anzahlVorhanden,
    onAngelegt,
    children,
    className,
}: PlanAnlegenProps): JSX.Element {
    const inputRef = useRef<HTMLInputElement>(null);
    const [zuschnitt, setZuschnitt] = useState<Zuschnitt | null>(null);
    const [laedt, setLaedt] = useState(false);
    /** Fehlermeldung als Hinweisleiste oben (kein Browser-Dialog). */
    const [meldung, setMeldung] = useState<string | null>(null);
    const laedtRef = useRef(false);

    // Der Upload dauert: danach mit dem AKTUELLEN Callback melden, nicht mit
    // dem vom Klick (sonst haengt der Aufrufer den Plan an einen veralteten
    // Planstand an).
    const onAngelegtRef = useRef(onAngelegt);
    useEffect(() => { onAngelegtRef.current = onAngelegt; }, [onAngelegt]);

    const oeffnen = () => {
        if (laedtRef.current || zuschnitt) return;
        setMeldung(null);
        inputRef.current?.click();
    };

    const dateiGewaehlt = (e: React.ChangeEvent<HTMLInputElement>) => {
        const datei = e.target.files?.[0];
        // Zuruecksetzen, damit dieselbe Datei erneut gewaehlt werden kann.
        e.target.value = '';
        if (!datei) return;
        const startName = planNameAusDatei(datei.name) || planRueckfallName(anzahlVorhanden);

        if (istPdf(datei)) {
            setZuschnitt({ art: 'pdf', datei, startName });
            return;
        }
        if (datei.type && !datei.type.startsWith('image/')) {
            setMeldung('Bitte ein Bild (JPG, PNG) oder eine PDF-Datei wählen.');
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
            const daten = ev.target?.result;
            if (typeof daten === 'string' && daten) setZuschnitt({ art: 'bild', daten, startName });
            else setMeldung(LESEFEHLER);
        };
        reader.onerror = () => setMeldung(LESEFEHLER);
        reader.readAsDataURL(datei);
    };

    const abbrechen = () => {
        if (laedtRef.current) return;
        setZuschnitt(null);
    };

    /** Zugeschnittenen Plan hochladen; bei Fehler bleibt der Zuschneider offen. */
    const uebernehmen = async (base64: string, name: string) => {
        if (laedtRef.current) return;
        laedtRef.current = true;
        setLaedt(true);
        setMeldung(null);
        try {
            const url = await uploadAsset(base64, projectId);
            if (!url) throw new Error('Upload lieferte keine Adresse');
            const plan: ProjectPlan = {
                id: crypto.randomUUID(),
                name: name.trim() || planRueckfallName(anzahlVorhanden),
                image: url,
                sectionId,
            };
            onAngelegtRef.current(plan);
            setZuschnitt(null);
        } catch (err) {
            console.error('Plan hochladen fehlgeschlagen', err);
            setMeldung('Der Plan konnte nicht hochgeladen werden. Bitte die Verbindung prüfen und erneut „Übernehmen“ tippen.');
        } finally {
            laedtRef.current = false;
            setLaedt(false);
        }
    };

    const ueberlagerung = (zuschnitt || meldung) && (
        // Klicks und Tasten im Zuschneider nicht an die Eltern im React-Baum
        // durchreichen (Portal!) — sonst setzt z. B. ein Tipp im Zuschneider
        // eine Nadel im Verortungsdialog darunter, oder Esc schliesst ihn.
        <div
            // Marke fuer den Verortungsdialog: solange sie im DOM steht, gehoert Esc dem
            // Zuschneider — auch wenn der Fokus auf body liegt und das Stoppen unten nicht greift.
            data-plan-zuschnitt={zuschnitt ? '' : undefined}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Escape') abbrechen();
            }}
        >
            {zuschnitt?.art === 'pdf' && (
                <PdfCropper file={zuschnitt.datei} startName={zuschnitt.startName} onCrop={uebernehmen} onCancel={abbrechen} />
            )}
            {zuschnitt?.art === 'bild' && (
                <PlanBildZuschnitt image={zuschnitt.daten} startName={zuschnitt.startName} onSave={uebernehmen} onCancel={abbrechen} />
            )}
            {meldung && (
                <div
                    role="alert"
                    className="fixed left-1/2 z-[150] flex w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 items-start gap-3 rounded-xl bg-red-600 px-4 py-3 text-white shadow-xl"
                    style={{ top: 'max(1rem, env(safe-area-inset-top))' }}
                >
                    <span className="flex-1 text-sm font-semibold">{meldung}</span>
                    <button
                        type="button"
                        onClick={() => setMeldung(null)}
                        className="-my-1 -mr-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg hover:bg-red-700"
                        aria-label="Hinweis schließen"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            )}
            {zuschnitt && laedt && (
                <div className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-900/60" role="status" aria-live="polite">
                    <div className="flex items-center gap-3 rounded-xl bg-slate-800 px-5 py-3 text-white shadow-xl">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span className="text-sm font-semibold">Plan wird hochgeladen …</span>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <>
            {children ? (
                children(oeffnen, laedt)
            ) : (
                <Button
                    type="button"
                    variant="secondary"
                    onClick={oeffnen}
                    loading={laedt}
                    icon={<FileUp className="w-4 h-4" />}
                    className={className}
                >
                    Plan hochladen
                </Button>
            )}
            <input
                type="file"
                ref={inputRef}
                className="hidden"
                accept="image/*,application/pdf"
                onChange={dateiGewaehlt}
            />
            {ueberlagerung && typeof document !== 'undefined' ? createPortal(ueberlagerung, document.body) : null}
        </>
    );
}
